import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClipboardList } from "@fortawesome/free-solid-svg-icons/faClipboardList";
import PageTitle from "../../Utils/smsTitle";
import { SubHeader } from "../../../Containers/SubHeader";
import { BackButton } from "../../Utils/Buttons/DataExportationButton";
import CustomTable from "../../Utils/Tables/CustomTable";
import { config } from "../../../Helpers/env";
import { logoutErrorMessage } from "../../../Helpers/ErrorMessages";
import { dateConvert, formatDate, formatDateTime } from "../../Utils/ConvertDate";
import { DataExportButton } from "../../Utils/Buttons/DataExportationButton";
import { useExcelReports, usePdfDownload } from "../../Utils/ReusableComponents/useExcelExport";
import { MainSearchComponent } from "../MainSearch";
import MainSnackbar from "../../Utils/Snackbar/SmsSnackbar";
import * as snackbarActions from "../../../actions/snackbarActions";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

const BaseGynObsReport = ({ title, endpoint, columns, actions, snackbars }) => {
  const [date, setDate] = useState({ start_date: dateConvert(), end_date: dateConvert() });
  const [submitted, setSubmitted] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [activePage, setActivePage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const records = 10;

  const handleChangeDate = (event) => {
    const { name, value } = event.target;
    setDate((prev) => ({ ...prev, [name]: value }));
  };

  const loadData = async () => {
    setSubmitted(true);
    if (!date.start_date || !date.end_date) return;
    setIsPending(true);
    try {
      const formData = new FormData();
      formData.append("start_date", date.start_date);
      formData.append("end_date", date.end_date);
      formData.append("search", searchValue);
      formData.append("per_page", records);
      const response = await axios.post(`${config.smsUrl}/${endpoint}/${activePage}`, formData);
      const payload = response?.data ?? {};
      setRows(Array.isArray(payload.rows) ? payload.rows : []);
      setTotalCount(payload?.total_count ?? 0);
      setHasLoadedOnce(true);
    } catch (error) {
      logoutErrorMessage(error, null, actions);
    } finally {
      setIsPending(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage, searchValue]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setActivePage(1);
    await loadData();
  };

  const handleSearch = (event) => {
    setSearchValue(event.target.value);
    setActivePage(1);
  };

  const handleChangeNextPage = (page) => {
    setActivePage(page);
  };

  const excelObj = {
    file_name: title,
    url: `${endpoint}-exports`,
    params: {
      start_date: date.start_date,
      end_date: date.end_date,
      search: searchValue,
      export_type: 2,
    },
  };
  const pdfObj = {
    file_name: title,
    url: `${endpoint}-exports`,
    params: {
      start_date: date.start_date,
      end_date: date.end_date,
      search: searchValue,
      export_type: 1,
    },
  };

  const { exportExcel, isLoading } = useExcelReports(actions, excelObj);
  const { exportPdf, isLoading: isLoadingPdf } = usePdfDownload(actions, pdfObj);

  const normalizedRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        patient_name: `${row.first_name || ""} ${row.last_name || ""}`.trim(),
      })),
    [rows]
  );

  const formatCellValue = (key, value) => {
    if (value === null || value === undefined || value === "") return "";
    if (key.includes("datetime")) return formatDateTime(value);
    if (key.includes("date")) return formatDate(value);
    return value;
  };

  const { openBar, type, message } = snackbars;

  return (
    <div className="journals">
      <PageTitle title={title} />
      <SubHeader title="Reports" subTitle={title}>
        <FontAwesomeIcon icon={faClipboardList} />
      </SubHeader>

      <div style={{ margin: "10px" }}>
        <BackButton text={"General Reports"} to={"/reports/generalreports"} />
      </div>

      <div className="general-ledger-header mb-3">
        <MainSearchComponent
          text=""
          type="date"
          changeDate={handleChangeDate}
          handleSubmit={handleSubmit}
          isSubmitted={submitted && (!date.start_date || !date.end_date)}
          end_date={date.end_date}
          start_date={date.start_date}
          submitted={submitted}
        >
          <DataExportButton
            title={title}
            downLoadPdf={exportPdf}
            exportCsv={exportExcel}
            isLoading={isLoading || isLoadingPdf}
          />
        </MainSearchComponent>
      </div>

      <div className="mui-tables">
        <MainSnackbar variant={type} open={openBar} message={message} handleCloseBar={() => actions.snackbarActions.hideSnackbar()} />
        <CustomTable
          title={title}
          customPage
          headData={[{ id: "si", numeric: false, disablePadding: false, label: "SI" }, ...columns.map((c) => ({ id: c.key, numeric: false, disablePadding: false, label: c.label }))]}
          handler={handleSearch}
          records={records}
          total_count={totalCount}
          activePage={activePage}
          handleChangeNextPage={handleChangeNextPage}
          data={normalizedRows}
          colSpan={columns.length + 1}
          term={searchValue}
        >
          {isPending && !hasLoadedOnce ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  <img src="/images/smsloader.gif" alt="loader" />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : normalizedRows.length > 0 ? (
            <TableBody>
              {normalizedRows.map((row, idx) => {
                const cnt = (activePage - 1) * records + idx + 1;
                return (
                  <TableRow key={`${row.id || idx}`} className="mui-table-row" hover role="checkbox" tabIndex={-1}>
                    <TableCell>{cnt}</TableCell>
                    {columns.map((column) => (
                      <TableCell key={`${column.key}-${idx}`}>{formatCellValue(column.key, row[column.key])}</TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell colSpan={columns.length + 1} align="center">
                  No records found
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </CustomTable>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return { snackbars: state.snackbar };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: {
      snackbarActions: bindActionCreators(snackbarActions, dispatch),
    },
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(BaseGynObsReport);